import { Grid, Cell } from 'components/Grid';
import styles from './sectionOverviews.scss';
import { IconSectionN } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionN';
import { IconSectionO } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionO';
import { IconSectionP } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionP';
import { IconSectionQ } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionQ';
import { IconSectionR } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionR';
import { IconSectionU } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionU';
import { IconSectionY } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionY';
import { IconSectionZ } from 'components/Shared/Icon/themes/BGH-RD3/iconSectionZ';
import { IconBrands } from 'components/Shared/Icon/themes/BGH-RD3/iconBrands';
import { connect, useSelector } from 'react-redux';
import { isMobile, isHuge } from 'components/Shared/responsive';
import { BhImage } from 'models/BhImage';
import { SectionOverview as SectionOverviewModel } from 'models/SectionOverview';
import { PageState } from 'models/PageState';
import { HeaderState } from 'models/HeaderState';
import { NO_ELEMENT } from 'utils';
import { CategoryLink } from 'components/Shared/CategoryLink';
import { Section } from 'components/Section';
import { selectOurBrands } from 'components/MainMenu/selectors';
import cn from 'classnames';
import { selectBlackFridayStatus } from 'components/Header/themes/BGH/selector';

type IconKeys = 'N' | 'O' | 'P' | 'Q' | 'R' | 'U' | 'Y' | 'Z' | 'I' | 'T';

interface IconSectionProps {
  iconKey: IconKeys;
  title: string;
  titleClassName: string;
  image?: BhImage;
  url?: string;
}

interface ConnectedProps {
  isMobile: boolean;
  isHuge: boolean;
  shouldUseWideImage?: boolean;
  brands?: {
    url: string;
    title: string;
  };
}

interface MergedProps extends ConnectedProps {
  sections: SectionOverviewModel[];
  title: string;
}

const icons = {
  N: IconSectionN,
  O: IconSectionO,
  P: IconSectionP,
  Q: IconSectionQ,
  T: IconSectionO,
  R: IconSectionR,
  U: IconSectionU,
  Y: IconSectionY,
  Z: IconSectionZ,
  I: IconBrands
};

const getBrandTitle = ({ title }: { title: string }) => {
  const [, brands] = title.split(' ');

  return brands ? brands : title;
};

const IconSection = ({ iconKey, title, titleClassName, url }: IconSectionProps) => {
  const blackFridayIsActive = useSelector(selectBlackFridayStatus);
  const Icon = icons[iconKey];
  const formatedTitle = title.split('&').join('&\n'); // To make all titles line break at the same place

  // Some Icons are undefined for other RD3 themes
  // Conditional check so it does not blow up
  const IconComponent = () =>
    Icon !== undefined ? (
      <div className={styles.iconContainer}>
        <Icon className={cn(styles.icon, { [styles.blackFriday]: blackFridayIsActive })} />
      </div>
    ) : (
      NO_ELEMENT
    );

  return (
    <CategoryLink
      titleClassName={titleClassName}
      appearance="tertiary"
      title={formatedTitle}
      categoryUrl={url}
      CustomComponent={IconComponent}
    />
  );
};

const _SectionOverviews = ({ isMobile, isHuge, brands, sections, title }: MergedProps) => {
  if (!sections) {
    return NO_ELEMENT;
  }

  const content = sections
    .filter(({ title }) => title !== 'Outlet')
    .map(({ title, url, articleGroup }) => (
      <Cell key={url}>
        <IconSection
          title={title}
          iconKey={articleGroup.slice(-1) as IconKeys}
          url={url}
          titleClassName={styles.title}
        />
      </Cell>
    ));

  if (isMobile && brands) {
    content.push(
      <Cell key={brands.url}>
        <IconSection
          title={getBrandTitle(brands)}
          iconKey="I"
          url={brands.url}
          titleClassName={styles.brandTitle}
        />
      </Cell>
    );
  }

  return (
    <Section title={<h1>{title}</h1>} className={styles.section}>
      <Grid
        className={cn({ [styles.center]: isHuge })}
        gutters={isHuge ? 'extraLarge' : isMobile ? 'small' : 'medium'}
        desktop="8"
        tablet="4"
        phone="3"
      >
        {content}
      </Grid>
    </Section>
  );
};

export const SectionOverviews = connect<ConnectedProps>(
  ({ currentBreakpoint, ...state }: HeaderState): ConnectedProps => ({
    isMobile: isMobile({ currentBreakpoint }),
    isHuge: isHuge({ currentBreakpoint }),
    brands: selectOurBrands(state as PageState)
  })
)(_SectionOverviews);
